import { zodResolver } from '@hookform/resolvers/zod';
import { Currency, EmployeeStatus, LegalEntityCountry } from '@seapoint/server/enums';
import { FormProvider, useForm } from 'react-hook-form';
import z from 'zod';
import { Button } from '../../../../../design-system/button/button';
import { Stack } from '../../../../../design-system/stack/stack';
import { EmployeeView } from '../../../../../types/entity-types';
import { AdditionalInformation } from './additional-information/additional-information';
import {
  AdditionalInformationFormValues,
  additionalInformationSchema,
} from './additional-information/additional-information.validation';
import { BankInformation } from './bank-information/bank-information';
import { BankInformationFormValues, bankInformationBaseSchema } from './bank-information/bank-information.validation';
import { Header } from './header/header';
import { PersonalInformation } from './personal-information/personal-information';
import {
  PersonalInformationFormValues,
  personalInformationSchema,
} from './personal-information/personal-information.validation';
import { SalaryInformation } from './salary-information/salary-information';
import {
  SalaryInformationFormValues,
  salaryInformationSchema,
} from './salary-information/salary-information.validation';

// Combined Employee Form Validation Schema with cross-field validation
const employeeFormBaseSchema = personalInformationSchema
  .merge(bankInformationBaseSchema)
  .merge(salaryInformationSchema)
  .merge(additionalInformationSchema);

const employeeFormSchema = employeeFormBaseSchema.superRefine((data, ctx) => {
  const isIBAN = data.country !== LegalEntityCountry.GB;

  if (isIBAN) {
    if (!data.iban) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['iban'],
        message: 'Missing IBAN',
      });
    }
  } else {
    if (!data.accountNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['accountNumber'],
        message: 'Missing account number',
      });
    }
    if (!data.sortCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sortCode'],
        message: 'Missing sort code',
      });
    }
  }
});

export type EmployeeFormValues = z.infer<typeof employeeFormSchema>;

type EmployeeFormProps = {
  employee?: EmployeeView;
  showBottomSaveButton?: boolean;
  legalEntityCountry: LegalEntityCountry;
  isSubmitting?: boolean;
  onSubmit: (data: EmployeeFormValues) => void;
  onClose: () => void;
};

export const EmployeeForm = ({
  employee,
  legalEntityCountry,
  showBottomSaveButton = false,
  isSubmitting = false,
  onSubmit,
  onClose,
}: EmployeeFormProps) => {
  const defaultValues = employee;

  const personalInfoDefaults: PersonalInformationFormValues = {
    firstName: defaultValues?.firstName ?? '',
    lastName: defaultValues?.lastName ?? '',
    status: defaultValues?.status ?? EmployeeStatus.ACTIVE,
  };

  const bankInfoDefaults: BankInformationFormValues = {
    country: defaultValues?.company?.legalEntity?.country ?? legalEntityCountry,
    currency:
      defaultValues?.destination?.currency ??
      (legalEntityCountry === LegalEntityCountry.GB ? Currency.GBP : Currency.EUR),
    accountNumber: defaultValues?.destination?.accountNumber,
    sortCode: defaultValues?.destination?.sortCode,
    iban: defaultValues?.destination?.iban,
  };

  const salaryInfoDefaults: SalaryInformationFormValues = {
    salary: defaultValues?.salary,
    taxId: defaultValues?.taxId,
  };

  const additionalInfoDefaults: AdditionalInformationFormValues = {
    role: defaultValues?.role ?? '',
    addressLine1: defaultValues?.address?.line1,
    addressLine2: defaultValues?.address?.line2,
    townCity: defaultValues?.address?.postTown,
    countyDistrict: defaultValues?.address?.countrySubDivision,
    postcode: defaultValues?.address?.postCode,
  };

  const form = useForm<EmployeeFormValues>({
    defaultValues: {
      ...personalInfoDefaults,
      ...bankInfoDefaults,
      ...salaryInfoDefaults,
      ...additionalInfoDefaults,
    },
    mode: 'onBlur',
    resolver: zodResolver(employeeFormSchema),
  });

  const handleSubmit = (data: EmployeeFormValues) => {
    onSubmit(data);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="h-full w-full">
        <Header employee={employee} onClose={onClose} />
        <Stack space={8} className="w-full px-4 pb-10">
          <PersonalInformation />

          <BankInformation legalEntityCountry={legalEntityCountry} />

          <AdditionalInformation />

          <SalaryInformation />

          {showBottomSaveButton && (
            <Button className="w-full" type="submit" disabled={!form.formState.isValid || isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Save'}
            </Button>
          )}
        </Stack>
      </form>
    </FormProvider>
  );
};
